import { ApiServiceErr } from '../../../api-http/apiService';
import { FormsSummary } from '../../../api-http/formResponses';
import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../../components/TableTheme';
import { PagingResponse } from '../../../types';
import { OrderEnum, SortEnum } from '../../../types/pagination.types';
import { UserRole } from '../../../types/user.types';
import { displayDate } from '../../../util/dateTime';
import { useIsUserRole } from '../../../util/useIsUserRole';
import { useSnack } from '../../../util/useSnack';
import {
  IconButton,
  LinearProgress,
  TablePagination,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { FC, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { PagingParams, TableProps } from '../../../types/form.types';
import { Eye } from 'react-feather';
import { managementTableSx } from '../../../components/ManagementPageLayout';

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

const { name, createdAt, updatedAt } = SortEnum;
const { asc } = OrderEnum;

interface TableProp {
  refresh: boolean;
  search: string;
  isLoading: boolean;
  isError: boolean;
  responses?: PagingResponse<FormsSummary>;
  refetch: () => void;
  error: ApiServiceErr | null;
  sortOnClick: (value: string) => void;
  pagingParams: PagingParams;
  tableProps: TableProps;
}

const FormResponsesTable: FC<TableProp> = ({
  refresh,
  search,
  isLoading,
  isError,
  responses,
  refetch,
  error,
  sortOnClick,
  pagingParams,
  tableProps,
}) => {
  const isViewOnly = useIsUserRole([UserRole.user, UserRole.dataEditor]);

  const { sortby, order } = pagingParams;
  const { showErrSnack } = useSnack();

  useEffect(() => {
    if (isError && error) {
      showErrSnack(error.msg);
    }
  }, [error]);

  useEffect(() => {
    refetch();
  }, [refresh, search]);

  return (
    <>
      {isLoading && <LinearProgress />}
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 700 }} aria-label="Form responses">
          <TableHead>
            <TableRow>
              <StyledTableCell align="left">
                <StyledTableSortLabel
                  active={sortby === name || true}
                  direction={sortby === name ? order : asc}
                  onClick={() => sortOnClick(name)}
                >
                  Response
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left" width={'20%'}>
                <StyledTableSortLabel
                  active={sortby === updatedAt || true}
                  direction={sortby === updatedAt ? order : asc}
                  onClick={() => sortOnClick(updatedAt)}
                >
                  Last updated
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="left" width={'20%'}>
                <StyledTableSortLabel
                  active={sortby === createdAt || true}
                  direction={sortby === createdAt ? order : asc}
                  onClick={() => sortOnClick(createdAt)}
                >
                  Created
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell align="center" width={'20%'}>
                Actions
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!responses || responses?.data.length === 0) && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                  No responses found
                </TableCell>
              </TableRow>
            )}
            {responses?.data.map((form) => {
              return (
                <TableRow key={form._id} sx={{ height: 56 }}>
                  <TableCell component="th" scope="row">
                    {isViewOnly ? (
                      form.name
                    ) : (
                      <NavLink to={`/forms-preview/${form._id}`}>
                        {form.name}
                      </NavLink>
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {displayDate(form.updatedAt)}
                  </TableCell>
                  <TableCell align="left">
                    {displayDate(form.createdAt)}
                  </TableCell>

                  <TableCell align="center">
                    <NavLink to={`/forms-preview/${form._id}`}>
                      <Tooltip title="Preview response">
                        <IconButton
                          aria-label={`Preview ${form.name}`}
                          sx={{ ...actionButtonSx, color: '#2563eb' }}
                        >
                          <Eye size={15} />
                        </IconButton>
                      </Tooltip>
                    </NavLink>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={responses?.pagination.total === 0}
          count={responses?.pagination.total || 0}
        />
      </TableContainer>
    </>
  );
};

export default FormResponsesTable;
